// "They booked on the link" (owner request, PJ, 2026-10-20): the Book panel's second way. The Calendly bookings the
// dialer hasn't linked, newest first with the likely ones on top ("Likely theirs": booked while this call was live or
// in the 30 minutes after it, or their email, phone or business-name answer fits this lead). Pick one, press "Link to
// <business>", and the call is saved as Meeting booked exactly like a booking made with "Book a time"
// (store.onMeetingBooked). The list asks again every 5 s for 2 minutes while open (Refresh asks now and starts again).
// A booking the dialer matched to this business by itself during the call ("Already in the dialer", review 2026-10-20)
// is listed too: its meeting exists with no call, and Link ties it to this call so the call saves as Meeting booked.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { QueueItem } from '../../../src/shared/api';
import type { CalendlyUnlinkedResponse, MeetingBookResponse } from '../../../src/shared/api-core';
import { ApiFailure, errorMessage } from '../api';
import { ErrorBanner, toast } from '../components/ui';
import { Spinner, useNow } from '../components/ui-extra';
import { endpoints } from './endpoints';
import { LINK_POLL_FOR_MS, LINK_POLL_MS, bookedAgo, bookingWho, keptPick, linkRequest, linkTarget, meetingWhen } from './linked';

export function LinkedBookings(props: { item: QueueItem; callId: number | null; onLinked: (m: MeetingBookResponse) => Promise<void>; onElsewhere: () => void }) {
  const b = props.item.business;
  const target = linkTarget(props.item, props.callId);
  const [data, setData] = useState<CalendlyUnlinkedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [round, setRound] = useState(0);          // Refresh starts a new 2 minutes
  const [polling, setPolling] = useState(true);
  const seq = useRef(0);
  const now = useNow(15_000);

  const load = () => {
    const mine = ++seq.current;
    endpoints.calendlyUnlinked(target).then((r) => {
      if (mine !== seq.current) return;
      setData(r);
      setError(null);
      setPicked((p) => keptPick(p, r.bookings));
    }, (e) => { if (mine === seq.current) setError(errorMessage(e)); });
  };
  useEffect(() => {
    load();
    setPolling(true);
    const until = Date.now() + LINK_POLL_FOR_MS;
    const t = setInterval(() => {
      if (Date.now() >= until) { clearInterval(t); setPolling(false); return; }
      load();
    }, LINK_POLL_MS);
    return () => { clearInterval(t); seq.current++; };
  }, [round, target.callId, target.membershipId, target.businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function link() {
    if (!picked || busy) return;
    const booking = data?.bookings.find((x) => x.inviteeUri === picked);
    setBusy(true);
    setLinkError(null);
    try {
      const meeting = await endpoints.linkCalendly(linkRequest(picked, target));
      toast(`Linked ${booking?.inviteeName ?? 'their'} booking to ${b.name}: ${meetingWhen(booking ?? { startTime: meeting.scheduledFor }, b.tz)}.`, 'ok');
      await props.onLinked(meeting);
    } catch (e) {
      // A 409 is a sentence for the setter (already linked, canceled, an open meeting on this lead): show it, re-read.
      setLinkError(errorMessage(e));
      if (e instanceof ApiFailure && e.status === 409) load();
    } finally {
      setBusy(false);
    }
  }

  const list = data?.bookings ?? [];
  return (
    <div class="vd-book-linked">
      <div class="row" style="justify-content:space-between;gap:8px;margin-bottom:6px">
        <span class="muted" style="font-size:13px">
          Bookings made on a Calendly link that the dialer hasn't linked yet{data ? `, last ${data.windowHours >= 48 ? `${Math.round(data.windowHours / 24)} days` : `${data.windowHours} hours`}` : ''}.
          {polling ? ' Checking every few seconds.' : ' Stopped checking: press Refresh.'}
        </span>
        <button type="button" class="btn btn-sm" onClick={() => setRound((r) => r + 1)}>Refresh</button>
      </div>
      {error ? <ErrorBanner error={`Couldn't load the bookings: ${error}`} /> : null}
      {data === null && !error ? <p class="muted"><Spinner /> Looking for their booking…</p> : null}
      {data && !data.calendlyConnected ? <p class="vd-field-hint" style="margin:0 0 6px">Calendly isn't connected, so older bookings may show without their details.</p> : null}
      {data && list.length === 0 ? (
        <div class="muted" style="font-size:14px">
          <p style="margin:4px 0">No booking to link yet. Bookings show here a few seconds after they're made: once they press Schedule in Calendly, wait a moment.</p>
          <p style="margin:4px 0">Nothing after a minute, or they booked another way? <button type="button" class="btn btn-sm" onClick={props.onElsewhere}>Booked elsewhere</button></p>
        </div>
      ) : null}
      {list.length ? (
        <div role="radiogroup" aria-label="Calendly bookings" class="stack" style="gap:4px">
          {list.map((x) => (
            <label class={`vd-linked-row ${picked === x.inviteeUri ? 'on' : ''}`} style="display:flex;gap:8px;align-items:flex-start;padding:6px;border-radius:6px;cursor:pointer">
              <input type="radio" name="vd-linked" checked={picked === x.inviteeUri} disabled={busy} onChange={() => { setPicked(x.inviteeUri); setLinkError(null); }} />
              <span style="flex:1;min-width:0">
                <strong>{bookingWho(x)}</strong>
                {x.result === 'matched'
                  ? <span class="chip chip-ok" style="margin-left:6px" title="The dialer matched this booking to the business by itself; Link ties it to this call.">Already in the dialer</span>
                  : x.likely ? <span class="chip chip-ok" style="margin-left:6px" title={x.likelyWhy.join('; ')}>Likely theirs</span> : null}
                <br />
                <span style="font-size:13px">{x.linkName} · {meetingWhen(x, b.tz)} · <span class="muted">{bookedAgo(x.receivedAt, now)}</span></span>
                {x.likelyWhy.length ? <><br /><span class="vd-field-hint">{x.likelyWhy.join(' · ')}</span></> : null}
                {x.detailsMissing ? <><br /><span class="vd-field-hint">Details not available from Calendly yet.</span></> : null}
                {x.inviteeEmail && x.inviteeName ? <><br /><span class="muted" style="font-size:12px">{x.inviteeEmail}</span></> : null}
              </span>
            </label>
          ))}
        </div>
      ) : null}
      <ErrorBanner error={linkError} />
      <div class="row" style="gap:8px;margin-top:8px">
        <button type="button" class="btn btn-primary" disabled={!picked || busy} onClick={() => void link()}>
          {busy ? 'Linking…' : `Link to ${b.name}`}
        </button>
        <span class="muted" style="font-size:13px">
          {props.callId === null ? 'Not linked to a call: the meeting is recorded on this lead.' : 'The call is logged as "Meeting booked".'}
        </span>
      </div>
    </div>
  );
}
